import { useRef, useState, MouseEvent as ReactMouseEvent } from "react"

import { Checkbox, FormControlLabel } from "@material-ui/core"

import Button from "ui/buttons/Button"
import { ReactComponent as PlusIcon } from "assets/icon-plus.svg"
import Popover from "@material-ui/core/Popover"
import { css } from "@emotion/css"
import { uniqueId } from "lodash/fp"
import { useI18n } from "providers/I18n"

export type FilterOption = {
  id: string
  labelKey: string
}

type Props = {
  options: FilterOption[]
  selected: string[]
  label?: string
  onSelect: (id: string) => void
}

export function FilterSelect(props: Props) {
  const { options, selected = [], label, onSelect } = props
  const { t } = useI18n()

  const { current: popoverId } = useRef(uniqueId("filter-select-"))
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleOpen = (event: ReactMouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const filtersOpen = Boolean(anchorEl)
  const filtersPopoverId = filtersOpen ? popoverId : undefined

  return (
    <>
      <Button
        variant="default"
        weight="default"
        color="primary"
        onClick={handleOpen}
        startIcon={<PlusIcon className={styles.btnIcon} />}
        className={styles.button}
      >
        {label ?? t("filters.label")}
      </Button>
      <Popover
        id={filtersPopoverId}
        open={filtersOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: "left",
        }}
      >
        <div className={styles.popover}>
          {options.map(({ id, labelKey }) => (
            <FormControlLabel
              key={id}
              control={
                <Checkbox
                  checked={selected.includes(id)}
                  onChange={() => {
                    onSelect(id)
                    handleClose()
                  }}
                  name={id}
                  color="primary"
                />
              }
              label={t(labelKey)}
            />
          ))}
        </div>
      </Popover>
    </>
  )
}

const styles = {
  popover: css`
    width: 263px;
    padding: 15px 17px;
    display: flex;
    flex-direction: column;
  `,
  btnIcon: css`
    height: 0.8rem;
    color: var(--primary-main);
  `,
  buttonsDivider: css`
    height: 32px;
    display: inline-block;
    margin: 0px var(--spacing-s) 0px var(--spacing-xxs);
    border-right: 1px solid var(--grey);
  `,
  button: css`
    &[data-color="primary"] {
      --btn-focus-ring-color: transparent;
    }
    :hover {
      background-color: transparent;
      border-color: transparent;
      color: var(--primary-dark);
    }
  `,
}
